<template lang="pug">
div.has-fixed-footer
    .alert.alert-warning(v-if="err") {{err.message}}
    .alert.alert-success(v-if="successMessage") {{successMessage}}

    .card
        .card-header
            h5.mb-0 User List
        .card-body
            .form-group
                input.form-control(type="text", v-model="searchText", placeholder="Filter by email or biobank")
            table.table.table-sm.table-striped
                thead
                    tr
                        th Email
                        th Biobank
                        th Active
                        th Admin
                        th Actions
                tbody
                    tr(v-for="user in filteredUsers", :key="user.email")
                        td {{user.email}}
                        td
                            select.form-control.form-control-sm(v-model="user._editBiobank")
                                option(value="") No biobank (admin only)
                                option(v-for="biobank in biobanks", :key="user.email + '-' + biobank.id", :value="biobank.id") {{biobank.id}}
                        td
                            input(type="checkbox", v-model="user._editActive")
                        td
                            input(type="checkbox", v-model="user._editAdmin")
                        td
                            button.btn.btn-primary.btn-sm.mr-1(@click="saveUser(user)", :disabled="!isUserDirty(user)") Save
                            button.btn.btn-secondary.btn-sm.mr-1(@click="startPasswordEdit(user)") Password
                            button.btn.btn-danger.btn-sm(@click="removeUser(user)") Delete

    .page-action-footer
        .container.page-action-footer-inner
            button.btn.btn-success(type="button", @click="openCreateDialog") Create User

    .modal.fade.show(v-if="showCreateDialog", tabindex="-1", role="dialog", style="display:block;background:rgba(0,0,0,0.4)")
        .modal-dialog(role="document")
            .modal-content
                .modal-header
                    h5.modal-title Create User
                    button.close(type="button", aria-label="Close", @click="closeCreateDialog")
                        span(aria-hidden="true") &times;
                form(@submit.prevent="createUser", autocomplete="off")
                    .modal-body
                        input.form-control.mb-2(
                            type="email",
                            placeholder="Email",
                            v-model="newUser.email",
                            name="new-user-email",
                            autocomplete="off",
                            autocapitalize="none",
                            spellcheck="false",
                            required
                        )
                        select.form-control.mb-2(v-model="newUser.biobank", :required="!newUser.is_admin")
                            option(value="__select__" disabled) Select a biobank
                            option(value="", v-if="newUser.is_admin") No biobank (admin only)
                            option(v-for="biobank in biobanks", :key="'new-user-'+biobank.id", :value="biobank.id") {{biobank.id}}
                        input.form-control.mb-2(
                            type="password",
                            placeholder="Password (min 8)",
                            v-model="newUser.password",
                            name="new-user-password",
                            autocomplete="new-password",
                            minlength="8",
                            required
                        )
                        .form-check.mb-2
                            input.form-check-input(type="checkbox", id="newUserActive", v-model="newUser.active")
                            label.form-check-label(for="newUserActive") Active
                        .form-check.mb-2
                            input.form-check-input(type="checkbox", id="newUserAdmin", v-model="newUser.is_admin")
                            label.form-check-label(for="newUserAdmin") Admin
                    .modal-footer
                        button.btn.btn-success(type="submit", :disabled="!canCreateUser") Create
                        button.btn.btn-secondary(type="button", @click="closeCreateDialog") Cancel

    .modal.fade.show(v-if="passwordEmail", tabindex="-1", role="dialog", style="display:block;background:rgba(0,0,0,0.4)")
        .modal-dialog(role="document")
            .modal-content
                .modal-header
                    h5.modal-title Set Password
                    button.close(type="button", aria-label="Close", @click="cancelPasswordEdit")
                        span(aria-hidden="true") &times;
                form(@submit.prevent="setPassword", autocomplete="off")
                    .modal-body
                        p.mb-2 Set password for #[strong {{passwordEmail}}]
                        input.form-control(
                            type="password",
                            placeholder="New password (min 8)",
                            minlength="8",
                            v-model="passwordValue",
                            name="reset-user-password",
                            autocomplete="new-password",
                            required
                        )
                    .modal-footer
                        button.btn.btn-primary(type="submit") Update Password
                        button.btn.btn-secondary(type="button", @click="cancelPasswordEdit") Cancel
</template>

<script>
import {
    getAdminUsers, createAdminUser, patchAdminUser, deleteAdminUser, getAdminBiobanks
} from "../../api";

function withEditableFields(users) {
    return users.map(user => ({
        ...user,
        _editBiobank: user.biobank,
        _editActive: !!user.active,
        _editAdmin: !!user.is_admin
    }));
}

export default {
    data() {
        return {
            err: null,
            successMessage: "",
            users: [],
            biobanks: [],
            searchText: "",
            passwordEmail: "",
            passwordValue: "",
            showCreateDialog: false,
            newUser: {
                email: "",
                biobank: "__select__",
                password: "",
                active: true,
                is_admin: false
            }
        };
    },
    computed: {
        canCreateUser() {
            const email = (this.newUser.email || "").trim();
            const password = this.newUser.password || "";
            const biobank = this.normalizedNewUserBiobank();
            if (!email) return false;
            if (!password || password.length < 8) return false;
            if (!this.newUser.is_admin && !biobank) return false;
            return true;
        },
        filteredUsers() {
            const needle = (this.searchText || "").trim().toLowerCase();
            if (!needle) return this.users;
            return this.users.filter(user =>
                (user.email || "").toLowerCase().includes(needle) ||
                (user._editBiobank || "").toLowerCase().includes(needle)
            );
        }
    },
    created() {
        this.reload();
    },
    methods: {
        normalizedNewUserBiobank() {
            const biobank = this.newUser.biobank || "";
            return biobank === "__select__" ? "" : biobank;
        },
        isUserDirty(user) {
            if (!user) return false;
            const originalBiobank = user.biobank || "";
            const editedBiobank = user._editBiobank || "";
            const originalActive = !!user.active;
            const editedActive = !!user._editActive;
            const originalAdmin = !!user.is_admin;
            const editedAdmin = !!user._editAdmin;
            return originalBiobank !== editedBiobank
                || originalActive !== editedActive
                || originalAdmin !== editedAdmin;
        },
        openCreateDialog() {
            this.showCreateDialog = true;
        },
        closeCreateDialog() {
            this.showCreateDialog = false;
            this.newUser = {email: "", biobank: "__select__", password: "", active: true, is_admin: false};
        },
        reload() {
            Promise.all([getAdminUsers(), getAdminBiobanks()])
                .then(([users, biobanks]) => {
                    this.err = null;
                    this.users = withEditableFields(users);
                    this.biobanks = biobanks;
                })
                .catch(err => this.err = err);
        },
        createUser() {
            const normalizedBiobank = this.normalizedNewUserBiobank();
            if (!this.newUser.is_admin && !normalizedBiobank) {
                this.err = {message: "Biobank is required for non-admin users"};
                return;
            }
            createAdminUser({
                ...this.newUser,
                biobank: normalizedBiobank
            })
                .then(() => {
                    this.successMessage = `User ${this.newUser.email} created`;
                    this.closeCreateDialog();
                    this.reload();
                })
                .catch(err => this.err = err);
        },
        saveUser(user) {
            if (!user._editAdmin && !user._editBiobank) {
                this.err = {message: "Biobank is required for non-admin users"};
                return;
            }
            patchAdminUser(user.email, {
                biobank: user._editBiobank,
                active: user._editActive,
                is_admin: user._editAdmin
            })
                .then(() => {
                    this.successMessage = `User ${user.email} updated`;
                    this.reload();
                })
                .catch(err => this.err = err);
        },
        removeUser(user) {
            if (!confirm(`Delete user "${user.email}"?`)) return;
            deleteAdminUser(user.email)
                .then(() => {
                    this.successMessage = `User ${user.email} deleted`;
                    this.reload();
                })
                .catch(err => this.err = err);
        },
        startPasswordEdit(user) {
            this.passwordEmail = user.email;
            this.passwordValue = "";
        },
        cancelPasswordEdit() {
            this.passwordEmail = "";
            this.passwordValue = "";
        },
        setPassword() {
            patchAdminUser(this.passwordEmail, {password: this.passwordValue})
                .then(() => {
                    this.successMessage = `Password updated for ${this.passwordEmail}`;
                    this.cancelPasswordEdit();
                })
                .catch(err => this.err = err);
        }
    }
};
</script>
